import {
    UseIconPreviewParams,
    mapIconPreviewIndoorStandLogoToInitialDate,
    mapIconPreviewIndoorStandPinToInitialDate,
    useIconPreview
} from 'hooks/marker/useIconPreview';
import { useUploadImage } from 'libs/hooks/io/useUploadImage';
import { useContext, useEffect } from 'react';
import { IIconV2WorldMarker, loadMapImages } from 'react-give-me-map';
import {
    IImageV2WorldMarker,
    IIndoorStandWorldMarker,
    ITextV2WorldMarker
} from 'react-give-me-map/dist/utils/world/worldTypes';
import { CtxEdit } from 'session/ctxEdit';
import styled from 'styled-components/macro';

interface SingleProps {
    marker: IIconV2WorldMarker | IImageV2WorldMarker | ITextV2WorldMarker | IIndoorStandWorldMarker;
    resoluton: 200 | 600 | 1000;
    resolutionY?: number;
    onImageCreated?: (imageUrl: string) => void;
    type: 'image' | 'icon';
    iconPrefix?: 'temp-icon|' | 'temp-icon';
    initialData: UseIconPreviewParams['initialData'];
}

const SingleLazyImageGenerator = (props: SingleProps) => {
    const ctxEdit = useContext(CtxEdit);
    const { uploaded, setFiles } = useUploadImage([props.resoluton], ctxEdit.map?.worldMapId);

    const iconPreview = useIconPreview({
        mapRef: ctxEdit.mapRef,
        worldMarkers: ctxEdit.worldMarkers,
        markerIds: [props.marker.id],
        onNewImageGenerated: ({ file }) => {
            setFiles([file]);
        },
        iconPrefix: props.iconPrefix,
        markerType: props.type,
        initialData: props.initialData
    });

    useEffect(() => {
        if (!uploaded?.data?.imageUrls) {
            return;
        }

        loadMapImages(ctxEdit.mapRef.current, [uploaded.data.imageUrls[props.resoluton]], () => {
            props.onImageCreated(uploaded.data.imageUrls[props.resoluton]);
        });
    }, [uploaded]);

    return (
        <S_Canvas
            ref={iconPreview.canvasRef}
            width={props.resoluton}
            height={props.resolutionY ?? props.resoluton}
        />
    );
};

export const LazyImageGenerator = () => {
    const ctxEdit = useContext(CtxEdit);

    if (!ctxEdit.mapRef?.current) {
        return;
    }

    const notFinishedIconMarkers = ctxEdit.worldMarkers.filter<IIconV2WorldMarker>(
        (e): e is IIconV2WorldMarker =>
            e.elementType === 'v2/icon' &&
            !ctxEdit.selectedMarkers.includes(e.id) &&
            e.elementData.imageUrl?.startsWith('temp-icon')
    );

    const notFinishedIndoorStandForPinMarkers =
        ctxEdit.worldMarkers.filter<IIndoorStandWorldMarker>(
            (e): e is IIndoorStandWorldMarker =>
                e.elementType === 'indoor_stand' &&
                !ctxEdit.selectedMarkers.includes(e.id) &&
                e.elementData.pinSrc?.startsWith('temp-icon')
        );

    const notFinishedIndoorStandForLogoMarkers =
        ctxEdit.worldMarkers.filter<IIndoorStandWorldMarker>(
            (e): e is IIndoorStandWorldMarker =>
                e.elementType === 'indoor_stand' &&
                !ctxEdit.selectedMarkers.includes(e.id) &&
                e.elementData.imageSrc?.startsWith('temp-icon')
        );

    const notFinishedImageMarkers = ctxEdit.worldMarkers.filter<IImageV2WorldMarker>(
        (e): e is IImageV2WorldMarker =>
            e.elementType === 'v2/image' &&
            !ctxEdit.selectedMarkers.includes(e.id) &&
            e.elementData.imageUrl?.startsWith('data:')
    );

    const notFinishedTextMarkers = ctxEdit.worldMarkers.filter<ITextV2WorldMarker>(
        (e): e is ITextV2WorldMarker =>
            e.elementType === 'v2/text' &&
            !ctxEdit.selectedMarkers.includes(e.id) &&
            e.elementData.imageUrl?.startsWith('data:')
    );
    return (
        <>
            {notFinishedIconMarkers.map((marker) => (
                <SingleLazyImageGenerator
                    key={marker.id}
                    marker={marker}
                    resoluton={200}
                    type="icon"
                    initialData={marker.elementData}
                    onImageCreated={(imageUrl) => {
                        ctxEdit.updateWorldMarkerData(marker.id, { imageUrl });
                    }}
                />
            ))}
            {notFinishedIndoorStandForPinMarkers.map((marker) => (
                <SingleLazyImageGenerator
                    key={marker.id}
                    marker={marker}
                    resoluton={200}
                    type="icon"
                    initialData={mapIconPreviewIndoorStandPinToInitialDate(marker.elementData)}
                    onImageCreated={(imageUrl) => {
                        ctxEdit.updateWorldMarkerData(marker.id, { pinSrc: imageUrl });
                    }}
                />
            ))}
            {notFinishedIndoorStandForLogoMarkers.map((marker) => (
                <SingleLazyImageGenerator
                    key={marker.id}
                    marker={marker}
                    resoluton={200}
                    resolutionY={90}
                    iconPrefix="temp-icon|"
                    type="icon"
                    initialData={mapIconPreviewIndoorStandLogoToInitialDate(marker.elementData)}
                    onImageCreated={(imageUrl) => {
                        ctxEdit.updateWorldMarkerData(marker.id, { imageSrc: imageUrl });
                    }}
                />
            ))}

            {notFinishedImageMarkers.map((marker) => (
                <SingleLazyImageGenerator
                    key={marker.id}
                    marker={marker}
                    resoluton={600}
                    type="image"
                    initialData={marker.elementData}
                    onImageCreated={(imageUrl) => {
                        ctxEdit.updateWorldMarkerData(marker.id, { imageUrl });
                    }}
                />
            ))}
            {notFinishedTextMarkers.map((marker) => (
                <SingleLazyImageGenerator
                    key={marker.id}
                    marker={marker}
                    resoluton={1000}
                    type="image"
                    initialData={marker.elementData}
                    onImageCreated={(imageUrl) => {
                        ctxEdit.updateWorldMarkerData(marker.id, { imageUrl });
                    }}
                />
            ))}
        </>
    );
};

const S_Canvas = styled.canvas`
    width: 100px;
    height: 100px;
    display: none;
`;
